<script>
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import IconNext from 'next/icon/Icon.vue';
import RichtextEditor from './components/RichtextEditor.vue';

export default {
  components: {
    RichtextEditor,
    IconNext,
  },
  props: {
    value: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    defaultValue: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    label: {
      type: String,
      default: '',
    },
    fallbackLabel: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
    },
    // TODO: Implement disabled prop
    // disabled: {
    //   type: Boolean,
    //   default: false,
    // },
    placeholder: {
      type: String,
      default: '',
    },
    variables: {
      type: Array,
      default: () => [],
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      v$: useVuelidate(),
      toLocale,
    };
  },
  data() {
    return {
      updatedValue: this.value ?? this.defaultValue,
      isFocused: false,
    };
  },
  computed: {
    descriptionTooltip() {
      if (!this.description) return null;

      return {
        content: this.toLocale(this.description),
        popperClass:
          'max-w-[300px] max-h-[300px] overflow-y-auto whitespace-pre-line',
        boundary: 'body',
        popperTriggers: ['hover'],
      };
    },
  },
  validations() {
    return {
      updatedValue: {
        required: requiredIf(this.required),
      },
    };
  },
  watch: {
    value(value) {
      this.updatedValue = value;
    },
  },
  mounted() {
    if (this.dirty) {
      this.$nextTick(() => {
        this.v$.$validate();
      });
    }
  },
  methods: {
    updateValue() {
      this.v$.$touch();
      this.$emit('change', this.updatedValue);
    },
    onBlur() {
      this.isFocused = false;
      this.v$.updatedValue.$touch();
      this.updateValue();
    },
    onFocus() {
      this.isFocused = true;
    },
  },
};
</script>

<template>
  <div
    class="relative flex flex-col min-w-0 gap-1"
    :class="{ error: v$.updatedValue.$error }"
  >
    <label
      v-if="label || descriptionTooltip"
      class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
    >
      <div v-if="label" class="block truncate">
        {{ toLocale(label, fallbackLabel) }}<span v-if="required">*</span>
      </div>
      <IconNext
        v-if="descriptionTooltip"
        v-tooltip.top="descriptionTooltip"
        icon="i-lucide-info"
        class="text-n-slate-11 size-3.5"
      />
    </label>

    <RichtextEditor
      v-model="updatedValue"
      class="richtext-editor"
      :class="{ focused: isFocused }"
      :placeholder="toLocale(placeholder)"
      :variables="variables"
      @blur="onBlur"
      @focus="onFocus"
    />

    <p
      v-if="v$.updatedValue.$error"
      class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
    >
      <template v-if="v$.updatedValue.required.$invalid">
        {{ $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.REQUIRED') }}
      </template>
      <template v-else>
        {{ $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.INVALID') }}
      </template>
    </p>
  </div>
</template>

<style lang="scss" scoped>
.richtext-editor {
  @apply px-2 rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out;
  @apply border border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak;
  ::v-deep .ProseMirror-menubar {
    @apply -ms-1;
  }
  &.focused {
    @apply border !border-n-brand !border-n-brand;
  }
}

.error .richtext-editor {
  @apply border-n-ruby-8 dark:border-n-ruby-8 hover:border-n-ruby-9 dark:hover:border-n-ruby-9 disabled:border-n-ruby-8 dark:disabled:border-n-ruby-8;
}
</style>
